/**
 * ReportScreen — visor del informe final de la reunión (Fase 55).
 *
 * Descarga report.md del bucket vía PAR, lo renderiza como HTML usando
 * marked y permite descargarlo. Solo lectura — la edición llega en P6.
 *
 * No modifica atoms del meeting (mismo patrón que ViewerScreen).
 */

import { useEffect, useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import styled from "styled-components";
import { marked } from "marked";

import { currentProcessingIdAtom } from "../../state/atoms.ts";
import { getProcessingFile, fetchTextFromPar } from "../../platform/api.ts";
import { downloadTextAsFile } from "../../platform/browser-fs";
import { Button } from "../components/Button.tsx";
import { useNavigateTo, useSyncParams } from "../routing.ts";

// ---------------------------------------------------------------------------
// Tipos de estado
// ---------------------------------------------------------------------------

type ReportState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "loaded"; markdown: string };

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export function ReportScreen() {
  const processingId = useAtomValue(currentProcessingIdAtom);
  const navigateTo = useNavigateTo();
  useSyncParams();
  const [state, setState] = useState<ReportState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!processingId) {
        setState({ kind: "error", message: "No hay reunión seleccionada." });
        return;
      }

      setState({ kind: "loading" });

      try {
        const { downloadUrl } = await getProcessingFile(processingId, "report.md");
        const text = await fetchTextFromPar(downloadUrl);
        if (!cancelled) setState({ kind: "loaded", markdown: text });
      } catch (err) {
        if (!cancelled) {
          setState({
            kind: "error",
            message: err instanceof Error ? err.message : String(err),
          });
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [processingId]);

  const html = useMemo(() => {
    if (state.kind !== "loaded") return "";
    try {
      return marked.parse(state.markdown, { async: false }) as string;
    } catch {
      return `<p>Error al renderizar el markdown.</p>`;
    }
  }, [state]);

  const handleBack = () => navigateTo.meetings();

  const handleDownload = () => {
    if (state.kind !== "loaded" || !processingId) return;
    downloadTextAsFile(`${processingId}-informe.md`, state.markdown);
  };

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  if (!processingId) {
    return (
      <Container>
        <ErrorText>No hay reunión seleccionada.</ErrorText>
        <Button $variant="ghost" onClick={handleBack}>← Volver</Button>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Button $variant="ghost" onClick={handleBack}>← Volver</Button>
        {state.kind === "loaded" && (
          <Button $variant="primary" onClick={handleDownload}>
            ↓ Descargar .md
          </Button>
        )}
      </Header>

      {state.kind === "loading" && <LoadingText>Cargando informe…</LoadingText>}

      {state.kind === "error" && (
        <ErrorBox>
          <ErrorTitle>Error al cargar el informe</ErrorTitle>
          <ErrorText>{state.message}</ErrorText>
          <Button $variant="ghost" onClick={handleBack}>← Volver a reuniones</Button>
        </ErrorBox>
      )}

      {state.kind === "loaded" && (
        <ReportBody dangerouslySetInnerHTML={{ __html: html }} />
      )}
    </Container>
  );
}

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.lg};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LoadingText = styled.p`
  color: ${({ theme }) => theme.color.textMuted};
  font-style: italic;
  text-align: center;
  padding: ${({ theme }) => theme.space.xxl} 0;
`;

const ErrorBox = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.space.xxl} 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.md};
  align-items: center;
`;

const ErrorTitle = styled.h2`
  color: ${({ theme }) => theme.color.error};
  font-size: ${({ theme }) => theme.font.size.lg};
  font-weight: ${({ theme }) => theme.font.weight.bold};
  margin: 0;
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.color.textMuted};
  margin: 0;
`;

const ReportBody = styled.div`
  background: ${({ theme }) => theme.color.surface};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.space.xl};

  /* Markdown content styling */
  font-size: ${({ theme }) => theme.font.size.md};
  line-height: 1.7;
  color: ${({ theme }) => theme.color.textPrimary};

  h1 {
    font-size: ${({ theme }) => theme.font.size.xxl};
    font-weight: ${({ theme }) => theme.font.weight.bold};
    margin: 0 0 ${({ theme }) => theme.space.lg} 0;
    padding-bottom: ${({ theme }) => theme.space.sm};
    border-bottom: 1px solid ${({ theme }) => theme.color.border};
  }

  h2 {
    font-size: ${({ theme }) => theme.font.size.xl};
    font-weight: ${({ theme }) => theme.font.weight.bold};
    margin: ${({ theme }) => theme.space.xl} 0 ${({ theme }) => theme.space.md} 0;
  }

  h3 {
    font-size: ${({ theme }) => theme.font.size.lg};
    font-weight: ${({ theme }) => theme.font.weight.bold};
    margin: ${({ theme }) => theme.space.lg} 0 ${({ theme }) => theme.space.sm} 0;
  }

  h4 {
    font-size: ${({ theme }) => theme.font.size.md};
    font-weight: ${({ theme }) => theme.font.weight.bold};
    margin: ${({ theme }) => theme.space.md} 0 ${({ theme }) => theme.space.xs} 0;
  }

  h5 {
    font-size: ${({ theme }) => theme.font.size.md};
    font-weight: ${({ theme }) => theme.font.weight.medium};
    margin: ${({ theme }) => theme.space.md} 0 ${({ theme }) => theme.space.xs} 0;
    color: ${({ theme }) => theme.color.textMuted};
  }

  p {
    margin: 0 0 ${({ theme }) => theme.space.md} 0;
  }

  ul, ol {
    margin: 0 0 ${({ theme }) => theme.space.md} 0;
    padding-left: ${({ theme }) => theme.space.xl};
  }

  li {
    margin-bottom: ${({ theme }) => theme.space.xs};
  }

  strong {
    font-weight: ${({ theme }) => theme.font.weight.bold};
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: ${({ theme }) => theme.space.md} 0 ${({ theme }) => theme.space.lg} 0;
    font-size: ${({ theme }) => theme.font.size.sm};
  }

  th {
    background: ${({ theme }) => theme.color.accentMuted};
    color: ${({ theme }) => theme.color.textPrimary};
    font-weight: ${({ theme }) => theme.font.weight.bold};
    text-align: left;
    padding: ${({ theme }) => theme.space.sm} ${({ theme }) => theme.space.md};
    border: 1px solid ${({ theme }) => theme.color.border};
  }

  td {
    padding: ${({ theme }) => theme.space.sm} ${({ theme }) => theme.space.md};
    border: 1px solid ${({ theme }) => theme.color.border};
    vertical-align: top;
  }

  tr:nth-child(even) td {
    background: ${({ theme }) => theme.color.background};
  }

  code {
    font-family: ${({ theme }) => theme.font.mono};
    font-size: ${({ theme }) => theme.font.size.xs};
    background: ${({ theme }) => theme.color.background};
    padding: 0.125rem 0.375rem;
    border-radius: ${({ theme }) => theme.radius.sm};
    border: 1px solid ${({ theme }) => theme.color.border};
  }

  pre {
    background: ${({ theme }) => theme.color.background};
    border: 1px solid ${({ theme }) => theme.color.border};
    border-radius: ${({ theme }) => theme.radius.md};
    padding: ${({ theme }) => theme.space.md};
    overflow-x: auto;
    margin: ${({ theme }) => theme.space.md} 0;

    code {
      background: none;
      border: none;
      padding: 0;
    }
  }

  hr {
    border: none;
    border-top: 1px solid ${({ theme }) => theme.color.border};
    margin: ${({ theme }) => theme.space.xl} 0;
  }

  blockquote {
    border-left: 3px solid ${({ theme }) => theme.color.accentMuted};
    margin: ${({ theme }) => theme.space.md} 0;
    padding: ${({ theme }) => theme.space.sm} ${({ theme }) => theme.space.md};
    color: ${({ theme }) => theme.color.textMuted};
    background: ${({ theme }) => theme.color.background};
    border-radius: 0 ${({ theme }) => theme.radius.sm} ${({ theme }) => theme.radius.sm} 0;
  }
`;
